import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from './components/Header';
import FormPanel from './components/FormPanel';
import PreviewPanel from './components/PreviewPanel';
import Dashboard from './components/Dashboard';
import ProjectHome from './components/ProjectHome';
import CompareView from './components/CompareView';
import TemplatePage from './components/TemplatePage';
import CoverLetterTemplatePage from './components/CoverLetterTemplatePage';
import CoverLetterBuilder from './components/CoverLetterBuilder';
import { onAuthChange, loadProjectsFromDB, saveProjectsToDB } from './firebaseClient';
import { TEMPLATES } from './data/templates';
import { DEFAULT_STYLES, DEFAULT_PAGE_SETTINGS, buildStylesFromTemplate } from './data/defaults';
import { COVER_LETTER_TEMPLATES, getCoverLetterTemplate } from './data/coverLetterTemplates';
import {
  makeProject, makeResumeSlot, makeCoverLetterSlot,
  upsertProject, removeProject,
  countTotals, MAX_RESUMES, MAX_COVER_LETTERS_TOTAL, MAX_COVER_LETTERS_PER_PROJECT,
  uniqueProjectName,
} from './data/projectStore';
import { parseResume } from './ai';
import Tour, { getResumeTourSteps, DASHBOARD_CL_START, DASHBOARD_PROJECT_START } from './components/Tour';
import { FeedbackTab } from './components/FeedbackWidget';
import './App.css';

const DEFAULT_RESUME = {
  personal: {
    name: 'John Smith', title: 'Senior Software Engineer',
    email: 'john.smith@email.com', phone: '+1 (555) 867-5309',
    location: 'Austin, TX', linkedin: 'linkedin.com/in/johnsmith',
    website: 'johnsmith.dev', photo: null,
    summary: 'Versatile Software Engineer with 8+ years of experience building scalable web applications.',
  },
  experience: [
    { id: 1, company: 'Orion Systems LLC', role: 'Senior Software Engineer', startDate: 'Jan 2022', endDate: '', current: true,
      bullets: ['Architected a distributed event-processing pipeline handling 12M+ daily transactions.'] },
    { id: 2, company: 'Vantage Loop Co.', role: 'Software Engineer', startDate: 'May 2019', endDate: 'Dec 2021', current: false,
      bullets: ['Built and maintained a customer-facing dashboard used by 30,000+ monthly active users.'] },
  ],
  education: [
    { id: 1, school: 'Westfield State University', degree: 'M.S.', field: 'Computer Science', startDate: '2015', endDate: '2017', gpa: '3.8' },
  ],
  skills: ['JavaScript', 'TypeScript', 'React', 'Node.js', 'Python', 'PostgreSQL', 'Docker', 'AWS'],
  misc: [],
};

const EMPTY_RESUME = {
  personal: { name: '', title: '', email: '', phone: '', location: '', linkedin: '', linkedinLabel: '', website: '', websiteLabel: '', photo: null, summary: '' },
  experience: [], education: [], skills: [], misc: [],
};

// ── Template sync modal ───────────────────────────────────
function TemplateSyncModal({ resumeTemplateName, coverLetterTemplateName, onSync, onKeep }) {
  return (
    <div className="nudge-overlay" onClick={onKeep}>
      <div className="nudge-modal" onClick={e => e.stopPropagation()}>
        <div className="nudge-icon">🔗</div>
        <h2 className="nudge-title">Update cover letter to match?</h2>
        <p className="nudge-body">
          You switched your resume to the <strong>{resumeTemplateName}</strong> template.
          Your linked cover letter can be updated to the matching <strong>{coverLetterTemplateName}</strong> style to keep everything consistent.
        </p>
        <div className="nudge-actions">
          <button className="btn-nudge-resume" onClick={onSync}>Yes, update cover letter</button>
          <button className="btn-nudge-proceed" onClick={onKeep}>No, keep current style</button>
        </div>
      </div>
    </div>
  );
}

// ── Cover letter picker modal ─────────────────────────────
function CoverLetterPickerModal({ coverLetters, onSelect, onClose }) {
  const getLabel = (cl, i) => cl.targetCompany?.trim() || `Cover Letter ${i + 1}`;
  return (
    <div className="nudge-overlay" onClick={onClose}>
      <div className="nudge-modal" onClick={e => e.stopPropagation()} style={{ gap: 12 }}>
        <div className="nudge-icon">✉️</div>
        <h2 className="nudge-title">Select a Cover Letter</h2>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          {coverLetters.map((cl, i) => (
            <button
              key={cl.id}
              onClick={() => onSelect(cl.id)}
              style={{
                width: '100%', textAlign: 'left', padding: '0 16px',
                background: 'var(--cream)', border: '1.5px solid var(--border)',
                borderRadius: 10, cursor: 'pointer', fontSize: 14,
                color: 'var(--ink)', fontFamily: 'inherit',
                transition: 'border-color 0.15s, background 0.15s',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                height: 56, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--teal)'; e.currentTarget.style.background = 'var(--teal-light, #f0fafa)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--cream)'; }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontWeight: 600, lineHeight: 1.3 }}>{getLabel(cl, i)}</div>
                {cl.targetRole?.trim() && <div style={{ fontSize: 12, color: 'var(--ink-faint)', marginTop: 2, lineHeight: 1.3 }}>{cl.targetRole.trim()}</div>}
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-faint)', background: 'var(--cream-dark)', padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {cl.templateName}
              </span>
            </button>
          ))}
        </div>
        <button onClick={onClose} style={{ marginTop: 4, background: 'none', border: 'none', color: 'var(--ink-faint)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Link cover letter to resume modal ────────────────────
function LinkToResumeModal({ resumeProjects, onSelect, onClose }) {
  return (
    <div className="nudge-overlay" onClick={onClose}>
      <div className="nudge-modal" onClick={e => e.stopPropagation()} style={{ gap: 12 }}>
        <div className="nudge-icon">🔗</div>
        <h2 className="nudge-title">Link to a Resume</h2>
        <p className="nudge-body" style={{ marginTop: -4 }}>Choose a resume project to link this cover letter to.</p>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
          {resumeProjects.map(proj => (
            <button
              key={proj.id}
              onClick={() => onSelect(proj.id)}
              style={{
                width: '100%', textAlign: 'left', padding: '0 16px',
                background: 'var(--cream)', border: '1.5px solid var(--border)',
                borderRadius: 10, cursor: 'pointer', fontSize: 14,
                color: 'var(--ink)', fontFamily: 'inherit',
                transition: 'border-color 0.15s, background 0.15s',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                height: 56, flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--teal)'; e.currentTarget.style.background = 'var(--teal-light, #f0fafa)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--cream)'; }}
            >
              <div style={{ fontWeight: 600, lineHeight: 1.3 }}>{proj.name}</div>
              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-faint)', background: 'var(--cream-dark)', padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {proj.resume?.templateName ?? 'Resume'}
              </span>
            </button>
          ))}
        </div>
        <button onClick={onClose} style={{ marginTop: 4, background: 'none', border: 'none', color: 'var(--ink-faint)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Import or start from scratch modal ───────────────────
function ImportOrScratchModal({ onScratch, onImport, onClose }) {
  const fileRef = React.useRef();
  return (
    <div className="nudge-overlay" onClick={onClose}>
      <div className="nudge-modal" onClick={e => e.stopPropagation()}>
        <div className="nudge-icon">📄</div>
        <h2 className="nudge-title">How would you like to start?</h2>
        <p className="nudge-body">Build your resume step by step, or upload an existing PDF and we'll extract your information automatically.</p>
        <div className="nudge-actions">
          <button className="btn-nudge-resume" onClick={() => fileRef.current.click()}>
            ↑ Import Existing PDF
          </button>
          <button className="btn-nudge-proceed" onClick={onScratch}>
            Start from Scratch
          </button>
        </div>
        <button className="btn-nudge-cancel" onClick={onClose}>Cancel</button>
        <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files[0]; if (f) { onClose(); onImport(f); } }} />
      </div>
    </div>
  );
}

export default function App() {
  // ── Auth ──────────────────────────────────────────────
  const [user,        setUser]        = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [lastSaved,   setLastSaved]   = useState(null);
  const [isSaving,    setIsSaving]    = useState(false);
  const [saveFailed,  setSaveFailed]  = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthChange((firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
      if (!firebaseUser) {
        setProjects([]);
        setActiveProjectId(null);
        setActiveCoverId(null);
        setAppReady(false);
        setView('dashboard');
        sessionStorage.removeItem('cedar_view_state');
      }
    });
    return () => unsubscribe();
  }, []);

  // ── Project state ─────────────────────────────────────
  const [projects,        setProjects]        = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [activeCoverId,   setActiveCoverId]   = useState(null);

  // ── View routing ──────────────────────────────────────
  const [view,          setView]          = useState('dashboard');
  const [compareMode,   setCompareMode]   = useState(false);
  const [compareTarget, setCompareTarget] = useState(null);
  const [fullAppMode,   setFullAppMode]   = useState(false);

  // ── Modal state ───────────────────────────────────────
  const [syncPrompt,        setSyncPrompt]        = useState(null);
  const [showImportOrScratch, setShowImportOrScratch] = useState(false);
  const [syncDestView, setSyncDestView] = useState('builder');
  const [showCLPicker, setShowCLPicker] = useState(false);
  const [linkCLState,  setLinkCLState]  = useState(null);

  // ── Tour state ────────────────────────────────────────
  const [showTour, setShowTour] = useState(false);
  const [tourMode, setTourMode] = useState(null); // 'dashboard' | 'editor' | null
  const tourTempProjectId = useRef(null);
  const [tourStep, setTourStep] = useState(0);

  // ── Working resume state ──────────────────────────────
  const [resume,                    setResume]                    = useState(EMPTY_RESUME);
  const [sectionStyles,             setSectionStyles]             = useState(DEFAULT_STYLES);
  const [pageSettings,              setPageSettings]              = useState(DEFAULT_PAGE_SETTINGS);
  const [history,                   setHistory]                   = useState([]);
  const [redoStack,                 setRedoStack]                 = useState([]);
  const [templateFeatures,          setTemplateFeatures]          = useState({ photo: false, photoPosition: false });
  const [densityPresets,             setDensityPresets]             = useState([]);
  const [selectedDensityLabel,       setSelectedDensityLabel]       = useState('Comfortable');
  const [templateDefaultStyles,     setTemplateDefaultStyles]     = useState(DEFAULT_STYLES);
  const [templateDefaultPageSettings, setTemplateDefaultPageSettings] = useState(DEFAULT_PAGE_SETTINGS);
  const [activeSection,             setActiveSection]             = useState('personal');
  const [uploadStatus,              setUploadStatus]              = useState(null);
  const [currentTemplateName,       setCurrentTemplateName]       = useState('Classic');
  const [currentTemplateId,         setCurrentTemplateId]         = useState('classic');
  const [numPages,                  setNumPages]                  = useState(1);

  const activeProject     = projects.find(p => p.id === activeProjectId) ?? null;
  const activeCoverLetter = activeProject?.coverLetters?.find(cl => cl.id === activeCoverId) ?? null;

  // ── Cloud sync ────────────────────────────────────────
  const [appReady, setAppReady] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setAppReady(true); return; }
    const localProjects = projects;
    loadProjectsFromDB(user.uid).then(cloudProjects => {
      let merged;
      if (cloudProjects) {
        const cloudIds  = new Set(cloudProjects.map(p => p.id));
        const localOnly = localProjects.filter(p => !cloudIds.has(p.id));
        merged = localOnly.length > 0 ? [...cloudProjects, ...localOnly] : cloudProjects;
      } else {
        saveProjectsToDB(user.uid, localProjects).catch(err => {
          console.error('Initial save failed:', err);
          setSaveFailed(true);
        });
        merged = localProjects;
      }
      setProjects(merged);
      try {
        const saved = sessionStorage.getItem('cedar_view_state');
        if (saved) {
          const { view: v, activeProjectId: pid, activeCoverId: cid } = JSON.parse(saved);
          const proj = merged.find(p => p.id === pid);
          if (proj) {
            setActiveProjectId(pid);
            if (proj.resume) {
              setResume(proj.resume.data ?? EMPTY_RESUME);
              setSectionStyles(proj.resume.sectionStyles ?? DEFAULT_STYLES);
              setPageSettings(proj.resume.pageSettings ?? DEFAULT_PAGE_SETTINGS);
              setCurrentTemplateName(proj.resume.templateName ?? 'Classic');
              setCurrentTemplateId(proj.resume.templateId ?? 'classic');
              const tmpl = TEMPLATES.find(t => t.id === (proj.resume.templateId ?? 'classic'));
              if (tmpl) {
                setTemplateFeatures(tmpl.features ?? { photo: false, photoPosition: false });
                setDensityPresets(tmpl.densityPresets ?? []);
                setSelectedDensityLabel('Comfortable');
                setTemplateDefaultStyles(buildStylesFromTemplate(tmpl));
                setTemplateDefaultPageSettings(tmpl.pageSettings);
              }
            }
            if (cid) setActiveCoverId(cid);
            setView(v);
          }
        }
      } catch (e) {
        sessionStorage.removeItem('cedar_view_state');
      }
      setAppReady(true);
    });
  }, [user, authLoading]); // eslint-disable-line

  // Autosave to Firestore (debounced 800ms)
  const pendingSaveRef = useRef(false);
  const retryTimerRef = useRef(null);
  useEffect(() => {
    if (!user) return;
    if (tourTempProjectId.current) { setIsSaving(false); return; }
    setIsSaving(true);
    setSaveFailed(false);
    pendingSaveRef.current = true;
    clearTimeout(retryTimerRef.current);
    const timer = setTimeout(async () => {
      try {
        await saveProjectsToDB(user.uid, projects);
        setIsSaving(false);
        setLastSaved(new Date());
        setSaveFailed(false);
        pendingSaveRef.current = false;
      } catch (err) {
        console.error('Autosave failed:', err);
        setIsSaving(false);
        setSaveFailed(true);
        // Retry once after 3 seconds (cleared if new save is triggered)
        retryTimerRef.current = setTimeout(async () => {
          try {
            await saveProjectsToDB(user.uid, projects);
            setSaveFailed(false);
            setLastSaved(new Date());
            pendingSaveRef.current = false;
          } catch (retryErr) {
            console.error('Autosave retry failed:', retryErr);
            pendingSaveRef.current = false;
          }
        }, 3000);
      }
    }, 800);
    return () => { clearTimeout(timer); clearTimeout(retryTimerRef.current); pendingSaveRef.current = false; };
  }, [projects, user]);

  // Warn before closing tab if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (pendingSaveRef.current || isSaving) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isSaving]);

  // Auto-save resume editor state → active project
  useEffect(() => {
    if (!activeProjectId) return;
    setProjects(prev => {
      const proj = prev.find(p => p.id === activeProjectId);
      if (!proj || !proj.resume) return prev;
      const shouldAutoRename =
        (proj.name === 'New Resume' || proj.name === 'New Project' || proj.name === 'Imported Resume') &&
        resume.personal.name;
      const autoName = shouldAutoRename
        ? uniqueProjectName(prev.filter(p => p.id !== activeProjectId), `${resume.personal.name.split(' ')[0]}'s Application`)
        : proj.name;
      return upsertProject(prev, {
        ...proj,
        name: autoName,
        resume: { ...proj.resume, templateId: currentTemplateId, templateName: currentTemplateName, sectionStyles, pageSettings, data: resume },
      });
    });
  }, [resume, sectionStyles, pageSettings, currentTemplateName, currentTemplateId, activeProjectId]); // eslint-disable-line

  const [tourFading, setTourFading] = useState(false);

  const handleCloseTour = useCallback(() => {
    // Dashboard mode: fade out with cream overlay, then clean up
    if (tourMode === 'dashboard' && tourTempProjectId.current) {
      setTourFading(true);
      setTimeout(() => {
        const tempId = tourTempProjectId.current;
        setShowTour(false);
        setTourMode(null);
        setTourStep(0);
        setProjects(prev => removeProject(prev, tempId));
        setActiveProjectId(null);
        setActiveCoverId(null);
        setView('dashboard');
        // Clear ref AFTER React processes the state updates so autosave guard still catches
        setTimeout(() => {
          tourTempProjectId.current = null;
          setTourFading(false);
        }, 50);
      }, 400);
    } else {
      setShowTour(false);
      setTourMode(null);
      setTourStep(0);
    }
  }, [tourMode]);

  const handleStartDashboardTour = useCallback(() => {
    if (tourTempProjectId.current || showTour) return; // prevent double-click
    const template = TEMPLATES.find(t => t.id === 'classic') ?? TEMPLATES[0];
    const merged = buildStylesFromTemplate(template);
    const proj = makeProject({ type: 'resume', name: uniqueProjectName(projects, 'Sample Resume') });
    const slot = makeResumeSlot({
      templateId: template.id,
      templateName: template.name,
      sectionStyles: merged,
      pageSettings: template.pageSettings,
    });
    slot.data = DEFAULT_RESUME;
    setProjects(prev => [...prev, { ...proj, resume: slot }]);
    setActiveProjectId(proj.id);
    tourTempProjectId.current = proj.id;
    setResume(DEFAULT_RESUME);
    setSectionStyles(merged);
    setPageSettings(template.pageSettings);
    setTemplateFeatures(template.features ?? { photo: false, photoPosition: false });
    setDensityPresets(template.densityPresets ?? []);
    setSelectedDensityLabel('Comfortable');
    setTemplateDefaultStyles({ ...DEFAULT_STYLES, ...merged });
    setTemplateDefaultPageSettings(template.pageSettings);
    setCurrentTemplateName(template.name);
    setCurrentTemplateId(template.id);
    setHistory([]);
    setRedoStack([]);
    setTourMode('dashboard');
    setView('builder');
    setTourStep(0);
    setShowTour(true);
  }, [projects, showTour]);

  const handleTourStepChange = useCallback((stepIndex) => {
    setTourStep(stepIndex);
    if (tourMode !== 'dashboard') return;

    if (stepIndex >= DASHBOARD_PROJECT_START && view !== 'project-home') {
      // Navigate to project home
      setView('project-home');
    } else if (stepIndex >= DASHBOARD_CL_START && stepIndex < DASHBOARD_PROJECT_START && view !== 'cl-builder') {
      // Create a linked cover letter (if not already created) and navigate to CL builder
      const proj = projects.find(p => p.id === activeProjectId);
      if (proj && proj.coverLetters.length === 0) {
        const clTemplate = getCoverLetterTemplate('classic');
        const cl = makeCoverLetterSlot({
          templateId: clTemplate.id,
          templateName: clTemplate.name,
          linkedToResume: true,
          defaultBodyFont: pageSettings.bodyFont ?? clTemplate.pageSettings.bodyFont ?? 'DM Sans',
        });
        cl.sectionStyles = clTemplate.styles;
        cl.pageSettings = { ...clTemplate.pageSettings, bodyFont: pageSettings.bodyFont ?? clTemplate.pageSettings.bodyFont ?? 'DM Sans' };
        setProjects(prev => {
          const p = prev.find(x => x.id === activeProjectId);
          if (!p) return prev;
          return upsertProject(prev, { ...p, coverLetters: [...p.coverLetters, cl] });
        });
        setActiveCoverId(cl.id);
      }
      setView('cl-builder');
    } else if (stepIndex < DASHBOARD_CL_START && view !== 'builder') {
      // Navigate back to resume builder
      setView('builder');
    }
  }, [tourMode, view, activeProjectId, pageSettings, projects]);

  // Clean up temp tour project if user navigates away (e.g. browser back)
  // Skip if handleCloseTour is already handling it (tourFading = true)
  useEffect(() => {
    const isTourView = view === 'builder' || view === 'cl-builder' || view === 'project-home';
    if (!isTourView && tourTempProjectId.current && !tourFading) {
      const tempId = tourTempProjectId.current;
      setShowTour(false);
      setTourMode(null);
      setTourStep(0);
      setActiveCoverId(null);
      setProjects(prev => removeProject(prev, tempId));
      setTimeout(() => { tourTempProjectId.current = null; }, 50);
    }
  }, [view, tourFading]);

  // ── Helpers ───────────────────────────────────────────
  const applyTemplate = useCallback((template) => {
    setHistory([]);
    setRedoStack([]);
    const merged = buildStylesFromTemplate(template);
    setSectionStyles(merged);
    setPageSettings(template.pageSettings);
    setTemplateFeatures(template.features ?? { photo: false, photoPosition: false });
    const newPresets = template.densityPresets ?? [];
    setDensityPresets(newPresets);
    // Reapply the user's density preference on the new template if it exists
    setSelectedDensityLabel(prev => {
      const label = prev ?? 'Comfortable';
      const match = newPresets.find(p => p.label === label);
      if (match) setTimeout(() => applyDensityPreset(match, true), 0);
      return label;
    });
    setTemplateDefaultStyles({ ...DEFAULT_STYLES, ...merged });
    setTemplateDefaultPageSettings(template.pageSettings);
    setCurrentTemplateName(template.name);
    setCurrentTemplateId(template.id);
  }, []);

  const loadProjectIntoEditor = useCallback((project) => {
    if (project.resume) {
      setResume(project.resume.data ?? EMPTY_RESUME);
      setSectionStyles(project.resume.sectionStyles ?? DEFAULT_STYLES);
      setPageSettings(project.resume.pageSettings   ?? DEFAULT_PAGE_SETTINGS);
      setCurrentTemplateName(project.resume.templateName ?? 'Classic');
      setCurrentTemplateId(project.resume.templateId     ?? 'classic');
      const tmpl = TEMPLATES.find(t => t.id === (project.resume.templateId ?? 'classic'));
      if (tmpl) {
        setTemplateFeatures(tmpl.features ?? { photo: false, photoPosition: false });
        setTemplateDefaultStyles(buildStylesFromTemplate(tmpl));
        setTemplateDefaultPageSettings(tmpl.pageSettings);
      }
    } else {
      setResume(EMPTY_RESUME);
      setSectionStyles(DEFAULT_STYLES);
      setPageSettings(DEFAULT_PAGE_SETTINGS);
      setCurrentTemplateName('Classic');
      setCurrentTemplateId('classic');
      setTemplateFeatures({ photo: false, photoPosition: false });
      setTemplateDefaultStyles(DEFAULT_STYLES);
    }
  }, []);

  const checkAndPromptSync = useCallback((newTemplate, destView) => {
    const proj = projects.find(p => p.id === activeProjectId);
    const linkedCLs = proj?.coverLetters?.filter(cl => cl.linkedToResume) ?? [];
    if (linkedCLs.length === 0) { setView(destView); return; }
    const matchingCLTemplate = getCoverLetterTemplate(newTemplate.id);
    const alreadyMatching = linkedCLs.every(cl => cl.templateId === matchingCLTemplate.id);
    if (alreadyMatching) { setView(destView); return; }
    setSyncDestView(destView);
    setView(destView);
    setSyncPrompt({ resumeTemplateName: newTemplate.name, matchingCLTemplate });
  }, [projects, activeProjectId]);

  const handleSyncCoverLetters = useCallback(() => {
    if (!syncPrompt) return;
    const { matchingCLTemplate } = syncPrompt;
    setProjects(prev => {
      const proj = prev.find(p => p.id === activeProjectId);
      if (!proj) return prev;
      const updatedCLs = proj.coverLetters.map(cl =>
        cl.linkedToResume
          ? { ...cl, templateId: matchingCLTemplate.id, templateName: matchingCLTemplate.name,
              sectionStyles: matchingCLTemplate.styles, pageSettings: matchingCLTemplate.pageSettings }
          : cl
      );
      return upsertProject(prev, { ...proj, coverLetters: updatedCLs });
    });
    setSyncPrompt(null);
    setView(syncDestView);
  }, [syncPrompt, activeProjectId, syncDestView]);

  const handleDismissSync = useCallback(() => {
    setSyncPrompt(null);
    setView(syncDestView);
  }, [syncDestView]);

  // ── Dashboard handlers ────────────────────────────────
  const handleOpenProject = useCallback((project) => {
    setActiveProjectId(project.id);
    loadProjectIntoEditor(project);
    setView('project-home');
  }, [loadProjectIntoEditor]);

  const handleDuplicateProject = useCallback((project) => {
    setProjects(prev => {
      const copy = {
        ...project,
        id:        Date.now(),
        name:      uniqueProjectName(prev, 'Copy of ' + project.name),
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      return [...prev, copy];
    });
  }, []);

  const handleDeleteProject = useCallback((id) => {
    setProjects(prev => removeProject(prev, id));
    if (activeProjectId === id) { setActiveProjectId(null); setView('dashboard'); }
  }, [activeProjectId]);

  const handleNewResume = useCallback(() => {
    const { resumes } = countTotals(projects);
    if (resumes >= MAX_RESUMES) return;
    const proj = makeProject({ type: 'resume', name: uniqueProjectName(projects, 'New Resume') });
    setProjects(prev => [...prev, proj]);
    setActiveProjectId(proj.id);
    setResume(EMPTY_RESUME);
    setSectionStyles(DEFAULT_STYLES);
    setPageSettings(DEFAULT_PAGE_SETTINGS);
    setCurrentTemplateName('Classic');
    setCurrentTemplateId('classic');
    setCompareMode(false);
    setView('template-select');
  }, [projects]);

  const handleNewCoverLetter = useCallback(() => {
    if (!activeProjectId) return;
    const proj = projects.find(p => p.id === activeProjectId);
    if (proj && proj.coverLetters.length >= MAX_COVER_LETTERS_PER_PROJECT) return;
    setActiveCoverId(null);
    setView('cl-template-select');
  }, [activeProjectId, projects]);

  const handleNewStandaloneCoverLetter = useCallback(() => {
    const { coverLetters } = countTotals(projects);
    if (coverLetters >= MAX_COVER_LETTERS_TOTAL) return;
    const proj = makeProject({ type: 'cover-letter', name: uniqueProjectName(projects, 'New Cover Letter') });
    setProjects(prev => [...prev, proj]);
    setActiveProjectId(proj.id);
    setActiveCoverId(null);
    setView('cl-template-select');
  }, [projects]);

  const handleLinkCoverLetterToResume = useCallback(() => {
    const { coverLetters } = countTotals(projects);
    if (coverLetters >= MAX_COVER_LETTERS_TOTAL) return;
    const linkable = projects.filter(p => p.resume && p.coverLetters.length < MAX_COVER_LETTERS_PER_PROJECT);
    if (linkable.length === 0) return;
    if (linkable.length === 1) {
      const proj = linkable[0];
      setActiveProjectId(proj.id);
      loadProjectIntoEditor(proj);
      setActiveCoverId(null);
      setView('cl-template-select');
    } else {
      setLinkCLState({ coverId: null, fromProjectId: null, mode: 'new-from-dashboard' });
    }
  }, [projects, loadProjectIntoEditor]);

  const handleNewFull = useCallback(() => {
    const { resumes } = countTotals(projects);
    if (resumes >= MAX_RESUMES) return;
    const proj = makeProject({ type: 'full', name: uniqueProjectName(projects, 'New Application') });
    setProjects(prev => [...prev, proj]);
    setActiveProjectId(proj.id);
    setResume(EMPTY_RESUME);
    setSectionStyles(DEFAULT_STYLES);
    setPageSettings(DEFAULT_PAGE_SETTINGS);
    setCurrentTemplateName('Classic');
    setCurrentTemplateId('classic');
    setCompareMode(false);
    setFullAppMode(true);
    setView('template-select');
  }, [projects]);

  // ── Cover letter flow ─────────────────────────────────
  const handleSelectCLTemplate = useCallback((clTemplate) => {
    if (!activeProjectId) return;
    const proj = projects.find(p => p.id === activeProjectId);
    if (!proj) return;
    const linkedToResume = !!proj.resume;
    const defaultBodyFont = linkedToResume
      ? (proj.resume.pageSettings?.bodyFont ?? clTemplate.pageSettings.bodyFont ?? 'DM Sans')
      : (clTemplate.pageSettings.bodyFont ?? 'DM Sans');

    if (activeCoverId) {
      const existingCL = proj.coverLetters.find(cl => cl.id === activeCoverId);
      if (existingCL) {
        const updated = {
          ...existingCL,
          templateId:    clTemplate.id,
          templateName:  clTemplate.name,
          sectionStyles: clTemplate.styles,
          pageSettings:  { ...clTemplate.pageSettings, bodyFont: defaultBodyFont },
        };
        setProjects(prev => {
          const p = prev.find(x => x.id === activeProjectId);
          if (!p) return prev;
          return upsertProject(prev, { ...p, coverLetters: p.coverLetters.map(cl => cl.id === activeCoverId ? updated : cl) });
        });
        setView('cl-builder');
        return;
      }
    }

    const cl = makeCoverLetterSlot({ templateId: clTemplate.id, templateName: clTemplate.name, linkedToResume, defaultBodyFont });
    cl.sectionStyles = clTemplate.styles;
    cl.pageSettings  = { ...clTemplate.pageSettings, bodyFont: defaultBodyFont };
    setProjects(prev => {
      const p = prev.find(x => x.id === activeProjectId);
      if (!p) return prev;
      if (p.coverLetters.length >= MAX_COVER_LETTERS_PER_PROJECT) return prev;
      return upsertProject(prev, { ...p, coverLetters: [...p.coverLetters, cl] });
    });
    setActiveCoverId(cl.id);
    if (fullAppMode) { setFullAppMode(false); setView('builder'); }
    else { setView('cl-builder'); }
  }, [activeProjectId, activeCoverId, projects, fullAppMode]);

  const handleUpdateCoverLetter = useCallback((updated) => {
    if (!activeProjectId) return;
    setProjects(prev => {
      const proj = prev.find(p => p.id === activeProjectId);
      if (!proj) return prev;
      return upsertProject(prev, { ...proj, coverLetters: proj.coverLetters.map(cl => cl.id === updated.id ? updated : cl) });
    });
  }, [activeProjectId]);

  const handleEditCoverLetter   = useCallback((clId) => { setActiveCoverId(clId); setView('cl-builder'); }, []);
  const handleDeleteCoverLetter = useCallback((clId) => {
    if (!activeProjectId) return;
    setProjects(prev => {
      const proj = prev.find(p => p.id === activeProjectId);
      if (!proj) return prev;
      return upsertProject(prev, { ...proj, coverLetters: proj.coverLetters.filter(cl => cl.id !== clId) });
    });
  }, [activeProjectId]);

  const handleUnlinkCoverLetter = useCallback((clId) => {
    if (!activeProjectId) return;
    setProjects(prev => {
      const proj = prev.find(p => p.id === activeProjectId);
      if (!proj) return prev;
      const cl = proj.coverLetters.find(c => c.id === clId);
      if (!cl) return prev;
      const newProjName = uniqueProjectName(prev, cl.targetCompany?.trim() ? `${cl.targetCompany.trim()} Cover Letter` : 'New Cover Letter');
      const newProj    = makeProject({ type: 'cover-letter', name: newProjName });
      const unlinkedCL = { ...cl, linkedToResume: false };
      const updatedCurrent = upsertProject(prev, { ...proj, coverLetters: proj.coverLetters.filter(c => c.id !== clId) });
      return [...updatedCurrent, { ...newProj, coverLetters: [unlinkedCL] }];
    });
  }, [activeProjectId]);

  // ── Resume template select handlers ──────────────────
  const handleSelectTemplate = useCallback((template) => {
    if (compareMode) {
      setCompareMode(false);
      setCompareTarget({ template, styles: buildStylesFromTemplate(template), pageSettings: template.pageSettings });
      setView('compare');
      return;
    }
    applyTemplate(template);
    const merged = buildStylesFromTemplate(template);
    setProjects(prev => {
      const proj = prev.find(p => p.id === activeProjectId);
      if (!proj) return prev;
      const updatedSlot = proj.resume
        ? { ...proj.resume, templateId: template.id, templateName: template.name, sectionStyles: merged, pageSettings: template.pageSettings }
        : { ...makeResumeSlot({ templateId: template.id, templateName: template.name, sectionStyles: merged, pageSettings: template.pageSettings }), data: resume };
      const updatedCLs = !proj.resume && proj.coverLetters.length > 0
        ? proj.coverLetters.map(cl => ({ ...cl, linkedToResume: true }))
        : proj.coverLetters;
      return upsertProject(prev, { ...proj, resume: updatedSlot, coverLetters: updatedCLs });
    });
    if (fullAppMode) { setView('cl-template-select'); }
    else { checkAndPromptSync(template, 'builder'); }
  }, [compareMode, applyTemplate, activeProjectId, resume, fullAppMode, checkAndPromptSync]);

  const handleBackFromTemplateSelect = useCallback(() => {
    if (compareMode) { setCompareMode(false); setView('builder'); return; }
    if (fullAppMode) {
      setFullAppMode(false);
      setProjects(prev => removeProject(prev, activeProjectId));
      setActiveProjectId(null);
      setView('dashboard');
      return;
    }
    const proj = projects.find(p => p.id === activeProjectId);
    if (proj && !proj.resume) {
      setProjects(prev => removeProject(prev, activeProjectId));
      setActiveProjectId(null);
      setView('dashboard');
    } else {
      setView(proj?.resume ? 'builder' : 'dashboard');
    }
  }, [compareMode, fullAppMode, projects, activeProjectId]);

  const handleBackFromCLTemplateSelect = useCallback(() => {
    if (fullAppMode) { setView('template-select'); return; }
    const proj = projects.find(p => p.id === activeProjectId);
    if (proj && !proj.resume && proj.coverLetters.length === 0) {
      setProjects(prev => removeProject(prev, activeProjectId));
      setActiveProjectId(null);
      setView('dashboard');
    } else {
      setView(proj?.resume ? 'project-home' : 'dashboard');
    }
  }, [fullAppMode, projects, activeProjectId]);

  const handleOpenCompare    = useCallback(() => { setCompareMode(true); setView('template-select'); }, []);
  const handleKeepCurrent    = useCallback(() => { setCompareTarget(null); setView('builder'); }, []);
  const handleSwitchTemplate = useCallback(() => {
    if (compareTarget) {
      applyTemplate(compareTarget.template);
      const merged = buildStylesFromTemplate(compareTarget.template);
      setProjects(prev => {
        const proj = prev.find(p => p.id === activeProjectId);
        if (!proj || !proj.resume) return prev;
        return upsertProject(prev, {
          ...proj,
          resume: { ...proj.resume, templateId: compareTarget.template.id, templateName: compareTarget.template.name, sectionStyles: merged, pageSettings: compareTarget.template.pageSettings },
        });
      });
      const newTemplate = compareTarget.template;
      setCompareTarget(null);
      checkAndPromptSync(newTemplate, 'builder');
    } else {
      setCompareTarget(null);
      setView('builder');
    }
  }, [compareTarget, applyTemplate, activeProjectId, checkAndPromptSync]);

  // ── Project home handlers ─────────────────────────────
  const handleUpdateProjectName = useCallback((name) => {
    setProjects(prev => {
      const proj = prev.find(p => p.id === activeProjectId);
      if (!proj) return prev;
      return upsertProject(prev, { ...proj, name });
    });
  }, [activeProjectId]);

  const handleEditResume = useCallback(() => {
    const proj = projects.find(p => p.id === activeProjectId);
    if (!proj) return;
    if (proj.resume) {
      setView('builder');
    } else {
      setResume(EMPTY_RESUME);
      setSectionStyles(DEFAULT_STYLES);
      setPageSettings(DEFAULT_PAGE_SETTINGS);
      setCurrentTemplateName('Classic');
      setCurrentTemplateId('classic');
      setShowImportOrScratch(true);
    }
  }, [projects, activeProjectId]);

  const handleDeleteResume = useCallback(() => {
    setProjects(prev => {
      const proj = prev.find(p => p.id === activeProjectId);
      if (!proj) return prev;
      return upsertProject(prev, { ...proj, resume: null });
    });
  }, [activeProjectId]);

  // ── PDF import ────────────────────────────────────────
  const handleUpload = useCallback(async (file) => {
    setUploadStatus('parsing');
    try {
      const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        fullText += content.items.map(item => item.str).join(' ') + '\n';
      }
      const parsed = await parseResume({ text: fullText });
      const withIds = {
        ...parsed,
        experience: (parsed.experience || []).map((e, i) => {
          const subRoles = (e.subRoles || []).map((sr, j) => ({ ...sr, id: Date.now() + i * 100 + j + 1 }));
          // If AI returned exactly one subRole, flatten it back to a regular entry
          // (single role at a company should never use subRoles format)
          if (subRoles.length === 1) {
            const sr = subRoles[0];
            return { ...e, id: e.id || Date.now() + i, role: sr.role || '', startDate: sr.startDate || '', endDate: sr.endDate || '', current: sr.current || false, bullets: (sr.bullets || []).length ? sr.bullets : [''], subRoles: [] };
          }
          return { ...e, id: e.id || Date.now() + i, subRoles };
        }),
        education: (parsed.education || []).map((e, i) => ({ ...e, id: e.id || Date.now() + i + 100 })),
        misc: (parsed.misc || []).map((sec, si) => ({
          ...sec,
          id:      sec.id || Date.now() + 200 + si,
          entries: (sec.entries || []).map((en, ei) => ({ ...en, id: Date.now() + 300 + si * 50 + ei })),
        })),
      };
      const proj = makeProject({ type: 'resume', name: uniqueProjectName(projects, 'Imported Resume') });
      setProjects(prev => [...prev, proj]);
      setActiveProjectId(proj.id);
      setResume(withIds);
      setUploadStatus(null);
      setView('template-select');
    } catch (err) {
      console.error('PDF import error:', err);
      setUploadStatus('error');
      setTimeout(() => setUploadStatus(null), 3000);
    }
  }, [projects]);

  // ── PDF import into existing project (project-home context) ─
  const handleUploadToActiveProject = useCallback(async (file) => {
    setUploadStatus('parsing');
    try {
      const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
      pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        fullText += content.items.map(item => item.str).join(' ') + '\n';
      }
      const parsed = await parseResume({ text: fullText });
      const withIds = {
        ...parsed,
        experience: (parsed.experience || []).map((e, i) => {
          const subRoles = (e.subRoles || []).map((sr, j) => ({ ...sr, id: Date.now() + i * 100 + j + 1 }));
          if (subRoles.length === 1) {
            const sr = subRoles[0];
            return { ...e, id: e.id || Date.now() + i, role: sr.role || '', startDate: sr.startDate || '', endDate: sr.endDate || '', current: sr.current || false, bullets: (sr.bullets || []).length ? sr.bullets : [''], subRoles: [] };
          }
          return { ...e, id: e.id || Date.now() + i, subRoles };
        }),
        education: (parsed.education || []).map((e, i) => ({ ...e, id: e.id || Date.now() + i + 100 })),
        misc: (parsed.misc || []).map((sec, si) => ({
          ...sec,
          id:      sec.id || Date.now() + 200 + si,
          entries: (sec.entries || []).map((en, ei) => ({ ...en, id: Date.now() + 300 + si * 50 + ei })),
        })),
      };
      setResume(withIds);
      setUploadStatus(null);
      setView('template-select');
    } catch (err) {
      console.error('PDF import error:', err);
      setUploadStatus('error');
      setTimeout(() => setUploadStatus(null), 3000);
    }
  }, []);

  // ── Session persistence ───────────────────────────────
  useEffect(() => {
    if (!appReady) return;
    if (tourTempProjectId.current) return; // don't persist temp tour session
    if (view !== 'dashboard' && activeProjectId) {
      sessionStorage.setItem('cedar_view_state', JSON.stringify({ view, activeProjectId, activeCoverId }));
    } else if (view === 'dashboard') {
      sessionStorage.removeItem('cedar_view_state');
    }
  }, [appReady, view, activeProjectId, activeCoverId]);

  // ── URL routing (back/forward button support) ────────
  const isPopRef = useRef(false);
  const urlSyncReady = useRef(false);
  const lastPathRef = useRef(window.location.pathname);

  const viewToPath = useCallback((v, pid, cid) => {
    if (!pid || v === 'dashboard') return '/';
    switch (v) {
      case 'project-home':       return `/p/${pid}`;
      case 'template-select':    return `/p/${pid}/templates`;
      case 'builder':            return `/p/${pid}/resume`;
      case 'compare':            return `/p/${pid}/compare`;
      case 'cl-template-select': return `/p/${pid}/cl-templates`;
      case 'cl-builder':         return cid ? `/p/${pid}/cl/${cid}` : `/p/${pid}`;
      default:                   return '/';
    }
  }, []);

  useEffect(() => {
    if (!appReady) return;
    if (tourTempProjectId.current) return; // don't update URL during dashboard tour
    if (isPopRef.current) { isPopRef.current = false; return; }
    const path = viewToPath(view, activeProjectId, activeCoverId);
    if (path === lastPathRef.current) return;
    lastPathRef.current = path;
    const state = { view, projectId: activeProjectId, coverId: activeCoverId };
    if (!urlSyncReady.current) {
      urlSyncReady.current = true;
      window.history.replaceState(state, '', path);
    } else {
      window.history.pushState(state, '', path);
    }
  }, [appReady, view, activeProjectId, activeCoverId, viewToPath]);

  useEffect(() => {
    const VALID_VIEWS = new Set(['dashboard','project-home','template-select','builder','compare','cl-template-select','cl-builder']);
    const handlePop = (e) => {
      isPopRef.current = true;
      if (e.state) {
        const { view: v, projectId, coverId } = e.state;
        if (!VALID_VIEWS.has(v)) { setView('dashboard'); lastPathRef.current = '/'; return; }
        lastPathRef.current = viewToPath(v, projectId, coverId);
        if (projectId && projectId !== activeProjectId) {
          const proj = projects.find(p => p.id === projectId);
          if (proj) {
            setActiveProjectId(projectId);
            loadProjectIntoEditor(proj);
          } else {
            lastPathRef.current = '/';
            window.history.replaceState({ view: 'dashboard', projectId: null, coverId: null }, '', '/');
            setActiveProjectId(null);
            setView('dashboard');
            return;
          }
        }
        if (!projectId) setActiveProjectId(null);
        if (v === 'cl-builder' && coverId) {
          const proj = projects.find(p => p.id === (projectId || activeProjectId));
          if (!proj?.coverLetters?.some(cl => cl.id === coverId)) {
            const fallbackPath = viewToPath('project-home', projectId || activeProjectId, null);
            lastPathRef.current = fallbackPath;
            window.history.replaceState({ view: 'project-home', projectId: projectId || activeProjectId, coverId: null }, '', fallbackPath);
            setView('project-home');
            return;
          }
        }
        if (v !== 'cl-template-select' && coverId !== undefined) setActiveCoverId(coverId);
        setView(v);
      } else {
        lastPathRef.current = '/';
        setActiveProjectId(null);
        setView('dashboard');
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [projects, activeProjectId, loadProjectIntoEditor, viewToPath]);

  // ── Resume mutation handlers ──────────────────────────
  const updatePersonal     = (f, v) => setResume(r => ({ ...r, personal: { ...r.personal, [f]: v } }));
  const addExperience      = ()      => setResume(r => ({ ...r, experience: [...r.experience, { id: Date.now(), company: '', role: '', startDate: '', endDate: '', current: false, bullets: [''], subRoles: [] }] }));
  const updateExperience   = (id, f, v) => setResume(r => ({ ...r, experience: r.experience.map(e => e.id === id ? { ...e, [f]: v } : e) }));
  const removeExperience   = (id)    => { pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel); setResume(r => ({ ...r, experience: r.experience.filter(e => e.id !== id) })); };
  const addEducation       = ()      => setResume(r => ({ ...r, education: [...r.education, { id: Date.now(), school: '', degree: '', field: '', startDate: '', endDate: '', gpa: '' }] }));
  const updateEducation    = (id, f, v) => setResume(r => ({ ...r, education: r.education.map(e => e.id === id ? { ...e, [f]: v } : e) }));
  const removeEducation    = (id)    => { pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel); setResume(r => ({ ...r, education: r.education.filter(e => e.id !== id) })); };
  const removeBullet       = (expId, idx) => { pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel); setResume(r => ({ ...r, experience: r.experience.map(e => e.id === expId ? { ...e, bullets: e.bullets.filter((_, i) => i !== idx) } : e) })); };
  const addSkill           = (s)     => { if (s && !resume.skills.includes(s)) { pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel); setResume(r => ({ ...r, skills: [...r.skills, s] })); } };
  const removeSkill        = (s)     => { pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel); setResume(r => ({ ...r, skills: r.skills.filter(k => k !== s) })); };
  const reorderSkills      = (from, to) => setResume(r => {
    const s = [...r.skills];
    const [moved] = s.splice(from, 1);
    s.splice(to, 0, moved);
    return { ...r, skills: s };
  });

  // ── Job match apply handlers (undo-aware) ─────────────
  const applyBulletSuggestion = useCallback((original, improved) => {
    const norm = s => s.trim().toLowerCase().replace(/[\u2018\u2019\u201C\u201D]/g, "'").replace(/[\u2013\u2014]/g, '-').replace(/\s+/g, ' ');
    const target = norm(original);
    const targetWords = target.split(' ').filter(w => w.length > 2);

    // Match: exact normalized match OR 75%+ word overlap
    const isMatch = (b) => {
      const nb = norm(b);
      if (nb === target) return true;
      if (targetWords.length < 3) return false;
      const bulletWords = new Set(nb.split(' ').filter(w => w.length > 2));
      const overlap = targetWords.filter(w => bulletWords.has(w)).length / targetWords.length;
      return overlap >= 0.75;
    };

    // Check if any bullet actually matches before pushing history
    const hasMatch = (resume.experience || []).some(exp =>
      exp.bullets.some(b => isMatch(b)) ||
      (exp.subRoles || []).some(sr => (sr.bullets || []).some(b => isMatch(b)))
    );
    if (!hasMatch) return;

    pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel);
    setResume(r => ({
      ...r,
      experience: r.experience.map(exp => ({
        ...exp,
        bullets: exp.bullets.map(b => isMatch(b) ? improved : b),
        subRoles: (exp.subRoles || []).map(sr => ({
          ...sr,
          bullets: (sr.bullets || []).map(b => isMatch(b) ? improved : b),
        })),
      })),
    }));
  }, [resume, sectionStyles, pageSettings, selectedDensityLabel]);

  const applySummarySuggestion = useCallback((newSummary) => {
    pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel);
    setResume(r => ({ ...r, personal: { ...r.personal, summary: newSummary } }));
  }, [resume, sectionStyles, pageSettings, selectedDensityLabel]);

  const applyAIBullets = useCallback((expId, newBullets) => {
    pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel);
    setResume(r => ({
      ...r,
      experience: r.experience.map(e => e.id === expId ? { ...e, bullets: newBullets } : e),
    }));
  }, [resume, sectionStyles, pageSettings, selectedDensityLabel]);

  const updateExperienceWithHistory = useCallback((id, f, v) => {
    pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel);
    setResume(r => ({ ...r, experience: r.experience.map(e => e.id === id ? { ...e, [f]: v } : e) }));
  }, [resume, sectionStyles, pageSettings, selectedDensityLabel]);

  // ── Section order & visibility ───────────────────────
  const reorderSections = (fromOrArray, to) => {
    setPageSettings(s => {
      // Accept either a final array or (from, to) indices
      if (Array.isArray(fromOrArray)) {
        return { ...s, sectionOrder: fromOrArray };
      }
      const order = [...(s.sectionOrder ?? ['experience','education','skills'])];
      const [moved] = order.splice(fromOrArray, 1);
      order.splice(to, 0, moved);
      return { ...s, sectionOrder: order };
    });
  };
  const toggleSectionVisibility = (key) => {
    pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel);
    setPageSettings(s => {
      const hidden = s.hiddenSections ?? [];
      return {
        ...s,
        hiddenSections: hidden.includes(key) ? hidden.filter(k => k !== key) : [...hidden, key],
      };
    });
  };

  // ── Misc section handlers ────────────────────────────
  const addMiscSection    = ()                      => {
    const newId = Date.now();
    setResume(r => ({ ...r, misc: [...(r.misc||[]), { id: newId, title: '', citeFmt: 'apa', entries: [{ id: newId+1, title: '', subtitle: '', date: '', description: '' }] }] }));
    setPageSettings(s => ({ ...s, sectionOrder: [...(s.sectionOrder ?? ['experience','education','skills']), 'misc:' + newId] }));
  };
  const updateMiscSection = (secId, field, val)     => setResume(r => ({ ...r, misc: (r.misc||[]).map(s => s.id === secId ? { ...s, [field]: val } : s) }));
  const updateMiscCiteFmt = (secId, fmt) => {
    pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel);
    setResume(r => ({ ...r, misc: (r.misc||[]).map(s => s.id === secId ? { ...s, citeFmt: fmt } : s) }));
  };

  const removeMiscSection = (secId)                 => {
    pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel);
    setResume(r => ({ ...r, misc: (r.misc||[]).filter(s => s.id !== secId) }));
    setPageSettings(s => ({
      ...s,
      sectionOrder:   (s.sectionOrder   ?? []).filter(k => k !== 'misc:' + secId),
      hiddenSections: (s.hiddenSections ?? []).filter(k => k !== 'misc:' + secId),
    }));
  };
  const addMiscEntry      = (secId)                 => setResume(r => ({ ...r, misc: (r.misc||[]).map(s => s.id === secId ? { ...s, entries: [...s.entries, { id: Date.now(), title: '', subtitle: '', date: '', description: '' }] } : s) }));
  const updateMiscEntry   = (secId, entId, f, val)  => setResume(r => ({ ...r, misc: (r.misc||[]).map(s => s.id === secId ? { ...s, entries: s.entries.map(e => e.id === entId ? { ...e, [f]: val } : e) } : s) }));
  const removeMiscEntry   = (secId, entId)          => { pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel); setResume(r => ({ ...r, misc: (r.misc||[]).map(s => s.id === secId ? { ...s, entries: s.entries.filter(e => e.id !== entId) } : s) })); };

  // Push a snapshot before any undoable change
  const pushHistory = useCallback((resumeSnap, stylesSnap, settingsSnap, densityLabelSnap) => {
    setHistory(h => [...h.slice(-29), {
      resume:             resumeSnap,
      sectionStyles:      stylesSnap,
      pageSettings:       settingsSnap,
      selectedDensityLabel: densityLabelSnap ?? null,
    }]);
    setRedoStack([]);
  }, []);

  const updateSectionStyle = (sec, f, v) => {
    setSectionStyles(s => {
      pushHistory(resume, s, pageSettings, selectedDensityLabel);
      return { ...s, [sec]: { ...s[sec], [f]: v } };
    });
  };
  const updatePageSetting = (f, v) => {
    setPageSettings(s => {
      pushHistory(resume, sectionStyles, s, selectedDensityLabel);
      return { ...s, [f]: v };
    });
  };
  // Batch updaters — push history ONCE then apply all changes atomically.
  // Used by section "Reset to defaults" buttons so one undo reverses the whole reset.
  // Silent live update for sliders — no history push during drag
  const updatePageSettingLive = (f, v) => setPageSettings(s => ({ ...s, [f]: v }));
  // Commit history after slider release — snapshot is the pre-drag state
  const commitPageSettingHistory = (preDragSnapshot) => {
    setHistory(h => [...h.slice(-29), {
      resume, sectionStyles, pageSettings: preDragSnapshot, selectedDensityLabel,
    }]);
    setRedoStack([]);
  };

  const batchSectionStyle = (sec, changes) => {
    setSectionStyles(s => {
      pushHistory(resume, s, pageSettings, selectedDensityLabel);
      return { ...s, [sec]: { ...s[sec], ...changes } };
    });
  };
  const batchPageSettings = (changes) => {
    setPageSettings(s => {
      pushHistory(resume, sectionStyles, s, selectedDensityLabel);
      return { ...s, ...changes };
    });
  };

  const applyDensityPreset = (preset, skipHistory = false) => {
    if (!skipHistory) pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel);
    setSelectedDensityLabel(preset.label);
    setPageSettings(s => ({ ...s, ...preset.pageSettings }));
    setSectionStyles(s => {
      const next = { ...s };
      Object.entries(preset.sectionStyles).forEach(([sec, vals]) => {
        next[sec] = { ...next[sec], ...vals };
      });
      return next;
    });
  };

  const handleResetToTemplateDefaults = useCallback(() => {
    const tmpl = TEMPLATES.find(t => t.id === currentTemplateId);
    if (!tmpl) return;
    const merged = buildStylesFromTemplate(tmpl);
    setHistory([]);
    setRedoStack([]);
    setSectionStyles(merged);
    setPageSettings(tmpl.pageSettings);
    setResume(r => ({ ...r, misc: (r.misc || []).map(s => ({ ...s, citeFmt: 'apa' })) }));
  }, [currentTemplateId]);

  const handleUndo = () => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    if (JSON.stringify({ r: prev.resume, s: prev.sectionStyles, p: prev.pageSettings }) ===
        JSON.stringify({ r: resume, s: sectionStyles, p: pageSettings })) {
      setHistory(h => h.slice(0, -1));
      return;
    }
    setRedoStack(r => [...r, { resume, sectionStyles, pageSettings, selectedDensityLabel }]);
    setSectionStyles(prev.sectionStyles);
    setPageSettings(prev.pageSettings);
    setResume(prev.resume);
    if (prev.selectedDensityLabel !== undefined) setSelectedDensityLabel(prev.selectedDensityLabel);
    setHistory(h => h.slice(0, -1));
  };
  const canUndo = history.length > 0;

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    if (JSON.stringify({ r: next.resume, s: next.sectionStyles, p: next.pageSettings }) ===
        JSON.stringify({ r: resume, s: sectionStyles, p: pageSettings })) {
      setRedoStack(r => r.slice(0, -1));
      return;
    }
    setHistory(h => [...h, { resume, sectionStyles, pageSettings, selectedDensityLabel }]);
    setSectionStyles(next.sectionStyles);
    setPageSettings(next.pageSettings);
    setResume(next.resume);
    if (next.selectedDensityLabel !== undefined) setSelectedDensityLabel(next.selectedDensityLabel);
    setRedoStack(r => r.slice(0, -1));
  };
  const canRedo = redoStack.length > 0;

  // ── Sub-role handlers ─────────────────────────────────
  const addSubRole = useCallback((expId) => {
    setResume(r => ({
      ...r,
      experience: r.experience.map(e => {
        if (e.id !== expId) return e;
        const existing = e.subRoles || [];
        // First click: migrate the flat entry into subRole #1, then add blank subRole #2
        if (existing.length === 0) {
          return {
            ...e,
            role: '', startDate: '', endDate: '', current: false, bullets: [''],
            subRoles: [
              { id: Date.now(), role: e.role || '', startDate: e.startDate || '', endDate: e.endDate || '', current: e.current || false, bullets: e.bullets?.length ? e.bullets : [''] },
              { id: Date.now() + 1, role: '', startDate: '', endDate: '', current: false, bullets: [''] },
            ],
          };
        }
        // Subsequent clicks: just add a blank subRole
        return { ...e, subRoles: [...existing, { id: Date.now(), role: '', startDate: '', endDate: '', current: false, bullets: [''] }] };
      }),
    }));
  }, []);

  const updateSubRole = useCallback((expId, subRoleId, field, value) => {
    setResume(r => ({
      ...r,
      experience: r.experience.map(e => e.id === expId
        ? { ...e, subRoles: (e.subRoles || []).map(sr => sr.id === subRoleId ? { ...sr, [field]: value } : sr) }
        : e
      ),
    }));
  }, []);

  const removeSubRole = useCallback((expId, subRoleId) => {
    pushHistory(resume, sectionStyles, pageSettings, selectedDensityLabel);
    setResume(r => ({
      ...r,
      experience: r.experience.map(e => {
        if (e.id !== expId) return e;
        const remaining = (e.subRoles || []).filter(sr => sr.id !== subRoleId);
        if (remaining.length === 1) {
          const sr = remaining[0];
          return { ...e, role: sr.role, startDate: sr.startDate, endDate: sr.endDate, current: sr.current, bullets: sr.bullets?.length ? sr.bullets : [''], subRoles: [] };
        }
        return { ...e, subRoles: remaining };
      }),
    }));
  }, [pushHistory, resume, sectionStyles, pageSettings]); // eslint-disable-line

  // ── Link cover letter to resume (from project home) ──
  const handleLinkCoverLetter = useCallback((coverId) => {
    const targets = projects.filter(p => p.resume && p.id !== activeProjectId && p.coverLetters.length < MAX_COVER_LETTERS_PER_PROJECT);
    if (targets.length === 0) {
      const hasResumeProjects = projects.some(p => p.resume && p.id !== activeProjectId);
      if (hasResumeProjects) {
        alert('All resume projects are at the 3 cover letter limit. Remove a cover letter first.');
      } else {
        alert('No resume projects found. Create a resume project first.');
      }
      return;
    }
    setLinkCLState({ coverId, fromProjectId: activeProjectId });
  }, [projects, activeProjectId]);

  const handleConfirmLink = useCallback((targetProjectId) => {
    if (!linkCLState) return;

    // New CL from dashboard linking directly to an existing resume project
    if (linkCLState.mode === 'new-from-dashboard') {
      const proj = projects.find(p => p.id === targetProjectId);
      if (!proj) { setLinkCLState(null); return; }
      setActiveProjectId(targetProjectId);
      loadProjectIntoEditor(proj);
      setActiveCoverId(null);
      setLinkCLState(null);
      setView('cl-template-select');
      return;
    }

    // Existing CL being moved to a resume project
    const { coverId, fromProjectId } = linkCLState;
    setProjects(prev => {
      const fromProj = prev.find(p => p.id === fromProjectId);
      const toProj   = prev.find(p => p.id === targetProjectId);
      if (!fromProj || !toProj) return prev;
      if (toProj.coverLetters.length >= MAX_COVER_LETTERS_PER_PROJECT) return prev;
      const cl = fromProj.coverLetters.find(c => c.id === coverId);
      if (!cl) return prev;
      const linkedCL    = { ...cl, linkedToResume: true };
      const updatedFrom = { ...fromProj, coverLetters: fromProj.coverLetters.filter(c => c.id !== coverId) };
      let result = upsertProject(prev, { ...toProj, coverLetters: [...toProj.coverLetters, linkedCL] });
      result = upsertProject(result, updatedFrom);
      if (updatedFrom.coverLetters.length === 0 && !updatedFrom.resume) {
        result = removeProject(result, fromProjectId);
        setActiveProjectId(targetProjectId);
        setView('project-home');
      }
      return result;
    });
    setLinkCLState(null);
  }, [linkCLState, projects, loadProjectIntoEditor]);

  // ── Loading screen ────────────────────────────────────
  if (!appReady) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--cream)' }}>
        <div style={{ fontSize: 14, color: 'var(--ink-faint)' }}>Loading…</div>
      </div>
    );
  }

  // ── Compute linkable projects for dashboard button ────
  const linkableProjects = projects.filter(p => p.resume && p.coverLetters.length < MAX_COVER_LETTERS_PER_PROJECT);

  // ── View routing ──────────────────────────────────────

  if (view === 'dashboard') {
    return (
      <>
        <Dashboard
          projects={projects}
          onOpenProject={handleOpenProject}
          onDeleteProject={handleDeleteProject}
          onDuplicateProject={handleDuplicateProject}
          onNewResume={handleNewResume}
          onNewCoverLetter={handleNewStandaloneCoverLetter}
          onNewFull={handleNewFull}
          onImport={handleUpload}
          onLinkCoverLetterToResume={linkableProjects.length > 0 ? handleLinkCoverLetterToResume : null}
          uploadStatus={uploadStatus}
          user={user}
          authLoading={authLoading}
          lastSaved={lastSaved}
          isSaving={isSaving} saveFailed={saveFailed}
          onStartTour={handleStartDashboardTour}
        />
        {uploadStatus === 'parsing' && (
          <div className="import-overlay">
            <div className="import-modal">
              <div className="import-modal-icon">📄</div>
              <h2 className="import-modal-title">Importing your resume…</h2>
              <p className="import-modal-sub">Reading and parsing your PDF. This takes a few seconds.</p>
              <div className="import-progress-track">
                <div className="import-progress-bar" />
              </div>
            </div>
          </div>
        )}
        {linkCLState?.mode === 'new-from-dashboard' && (
          <LinkToResumeModal
            resumeProjects={linkableProjects}
            onSelect={handleConfirmLink}
            onClose={() => setLinkCLState(null)}
          />
        )}
        {tourFading && <div className="tour-fade-overlay" />}
      </>
    );
  }

  if (view === 'project-home' && activeProject) {
    return (
      <>
        {showImportOrScratch && (
          <ImportOrScratchModal
            onClose={() => setShowImportOrScratch(false)}
            onScratch={() => { setShowImportOrScratch(false); setView('template-select'); }}
            onImport={(file) => { setShowImportOrScratch(false); handleUploadToActiveProject(file); }}
          />
        )}
        {uploadStatus === 'parsing' && (
          <div className="import-overlay">
            <div className="import-modal">
              <div className="import-modal-icon">📄</div>
              <h2 className="import-modal-title">Importing your resume…</h2>
              <p className="import-modal-sub">Reading and parsing your PDF. This takes a few seconds.</p>
              <div className="import-progress-track">
                <div className="import-progress-bar" />
              </div>
            </div>
          </div>
        )}
        <ProjectHome
          project={activeProject}
          onBackToProjects={() => {
            if (!activeProject.resume && activeProject.coverLetters.length === 0) {
              setProjects(prev => removeProject(prev, activeProjectId));
              setActiveProjectId(null);
            }
            setView('dashboard');
          }}
          onEditResume={handleEditResume}
          onUpdateName={handleUpdateProjectName}
          onDeleteResume={handleDeleteResume}
          onAddCoverLetter={handleNewCoverLetter}
          onEditCoverLetter={handleEditCoverLetter}
          onDeleteCoverLetter={handleDeleteCoverLetter}
          onLinkCoverLetter={handleLinkCoverLetter}
          onUnlinkCoverLetter={handleUnlinkCoverLetter}
          user={user}
          lastSaved={lastSaved}
          isSaving={isSaving} saveFailed={saveFailed}
        />
        {linkCLState && !linkCLState.mode && (
          <LinkToResumeModal
            resumeProjects={projects.filter(p => p.resume && p.id !== activeProjectId && p.coverLetters.length < MAX_COVER_LETTERS_PER_PROJECT)}
            onSelect={handleConfirmLink}
            onClose={() => setLinkCLState(null)}
          />
        )}
        {showTour && tourMode === 'dashboard' && (
          <Tour
            steps={getResumeTourSteps('dashboard')}
            onClose={handleCloseTour}
            currentStep={tourStep}
            onStepChange={handleTourStepChange}
          />
        )}
        {tourFading && <div className="tour-fade-overlay" />}
        <FeedbackTab />
      </>
    );
  }

  if (view === 'template-select') {
    return (
      <TemplatePage
        compareMode={compareMode}
        fullAppMode={fullAppMode}
        onSelectTemplate={handleSelectTemplate}
        onBack={handleBackFromTemplateSelect}
        disabledTemplateId={activeProject?.resume ? currentTemplateId : null}
        onHome={() => {
          if (fullAppMode) {
            setFullAppMode(false);
            setProjects(prev => removeProject(prev, activeProjectId));
            setActiveProjectId(null);
          } else {
            const proj = projects.find(p => p.id === activeProjectId);
            if (proj && !proj.resume) {
              setProjects(prev => removeProject(prev, activeProjectId));
              setActiveProjectId(null);
            }
          }
          setView('dashboard');
        }}
        defaultResume={DEFAULT_RESUME}
        user={user}
        lastSaved={lastSaved}
        isSaving={isSaving} saveFailed={saveFailed}
      />
    );
  }

  if (view === 'cl-template-select') {
    return (
      <CoverLetterTemplatePage
        onSelectTemplate={handleSelectCLTemplate}
        onBack={handleBackFromCLTemplateSelect}
        disabledTemplateId={activeCoverId ? activeCoverLetter?.templateId : null}
        onHome={() => {
          if (fullAppMode) { setFullAppMode(false); }
          const proj = projects.find(p => p.id === activeProjectId);
          if (proj && !proj.resume && proj.coverLetters.length === 0) {
            setProjects(prev => removeProject(prev, activeProjectId));
            setActiveProjectId(null);
          }
          setView('dashboard');
        }}
        resumeTemplateId={activeProject?.resume?.templateId ?? null}
        fullAppMode={fullAppMode}
        user={user}
        lastSaved={lastSaved}
        isSaving={isSaving} saveFailed={saveFailed}
      />
    );
  }

  if (view === 'cl-builder' && activeCoverLetter) {
    const clTemplate = COVER_LETTER_TEMPLATES.find(t => t.id === activeCoverLetter.templateId) ?? COVER_LETTER_TEMPLATES[0];
    const resumeData = activeCoverLetter.linkedToResume && activeProject?.resume ? activeProject.resume.data : null;
    const personName = resumeData?.personal?.name || activeCoverLetter?.standaloneInfo?.name || 'cover_letter';
    const clPageSettings = activeCoverLetter.pageSettings ?? clTemplate.pageSettings;
    return (
      <>
        <CoverLetterBuilder
          coverLetter={{ ...activeCoverLetter, pageSettings: clPageSettings }}
          resumeData={resumeData}
          templateStyles={clTemplate.styles}
          pageSettings={clPageSettings}
          templateDefaultPageSettings={clTemplate.pageSettings}
          templateName={clTemplate.name}
          onBack={() => setView('project-home')}
          onChange={handleUpdateCoverLetter}
          personName={personName}
          user={user}
          lastSaved={lastSaved}
          isSaving={isSaving} saveFailed={saveFailed}
          onGoToResume={activeProject?.resume ? () => setView('builder') : () => setView('template-select')}
          onChangeTemplate={() => setView('cl-template-select')}
          onHome={() => setView('dashboard')}
          onUndo={handleUndo}
          canUndo={canUndo}
          onRedo={handleRedo}
          canRedo={canRedo}
        />
        {showTour && tourMode === 'dashboard' && (
          <Tour
            steps={getResumeTourSteps('dashboard')}
            onClose={handleCloseTour}
            currentStep={tourStep}
            onStepChange={handleTourStepChange}
          />
        )}
        {tourFading && <div className="tour-fade-overlay" />}
        <FeedbackTab />
      </>
    );
  }

  if (view === 'compare' && compareTarget) {
    return (
      <CompareView
        resume={resume}
        currentStyles={sectionStyles}
        currentPageSettings={pageSettings}
        currentTemplateName={currentTemplateName}
        proposedTemplate={compareTarget.template}
        proposedStyles={compareTarget.styles}
        proposedPageSettings={compareTarget.pageSettings}
        onKeep={handleKeepCurrent}
        onSwitch={handleSwitchTemplate}
        onHome={() => setView('dashboard')}
        user={user}
        lastSaved={lastSaved}
        isSaving={isSaving} saveFailed={saveFailed}
      />
    );
  }

  // ── Builder view ──────────────────────────────────────
  return (
    <div className="app">
      <Header onHome={() => setView('dashboard')} user={user} lastSaved={lastSaved} isSaving={isSaving} saveFailed={saveFailed} />
      <div className="workspace">
        <FormPanel
          resume={resume}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          updatePersonal={updatePersonal}
          addExperience={addExperience}
          updateExperience={updateExperience}
          removeExperience={removeExperience}
          removeBullet={removeBullet}
          addSubRole={addSubRole}
          updateSubRole={updateSubRole}
          removeSubRole={removeSubRole}
          addEducation={addEducation}
          updateEducation={updateEducation}
          removeEducation={removeEducation}
          addSkill={addSkill}
          removeSkill={removeSkill}
          reorderSkills={reorderSkills}
          addMiscSection={addMiscSection}
          updateMiscSection={updateMiscSection}
          updateMiscCiteFmt={updateMiscCiteFmt}
          removeMiscSection={removeMiscSection}
          addMiscEntry={addMiscEntry}
          updateMiscEntry={updateMiscEntry}
          removeMiscEntry={removeMiscEntry}
          sectionStyles={sectionStyles}
          updateSectionStyle={updateSectionStyle}
          pageSettings={pageSettings}
          updatePageSetting={updatePageSetting}
          templateFeatures={templateFeatures}
          templateDefaultStyles={templateDefaultStyles}
          templateDefaultPageSettings={templateDefaultPageSettings}
          densityPresets={densityPresets}
          applyDensityPreset={applyDensityPreset}
          selectedDensityLabel={selectedDensityLabel}
          batchSectionStyle={batchSectionStyle}
          batchPageSettings={batchPageSettings}
          updatePageSettingLive={updatePageSettingLive}
          commitPageSettingHistory={commitPageSettingHistory}
          reorderSections={reorderSections}
          toggleSectionVisibility={toggleSectionVisibility}
          onResetToTemplateDefaults={handleResetToTemplateDefaults}
          applyBulletSuggestion={applyBulletSuggestion}
          applySummarySuggestion={applySummarySuggestion}
          applyAIBullets={applyAIBullets}
          updateExperienceWithHistory={updateExperienceWithHistory}
          numPages={numPages}
        />
        <PreviewPanel
          resume={resume}
          sectionStyles={sectionStyles}
          pageSettings={pageSettings}
          onChangeTemplate={() => { setCompareMode(false); setView('template-select'); }}
          onCompareTemplate={handleOpenCompare}
          onBackToProject={() => setView('project-home')}
          onUndo={handleUndo}
          canUndo={canUndo}
          onRedo={handleRedo}
          canRedo={canRedo}
          onGoToCoverLetter={() => {
            const cls = activeProject?.coverLetters ?? [];
            if (cls.length === 0)      { setView('cl-template-select'); }
            else if (cls.length === 1) { setActiveCoverId(cls[0].id); setView('cl-builder'); }
            else                       { setShowCLPicker(true); }
          }}
          onPageCount={setNumPages}
          onStartTour={() => { setTourMode('editor'); setTourStep(0); setShowTour(true); }}
        />
      </div>

      {showTour && (
        <Tour
          steps={getResumeTourSteps(tourMode)}
          onClose={handleCloseTour}
          currentStep={tourStep}
          onStepChange={handleTourStepChange}
        />
      )}

      {showCLPicker && (
        <CoverLetterPickerModal
          coverLetters={activeProject?.coverLetters ?? []}
          onSelect={(id) => { setActiveCoverId(id); setView('cl-builder'); setShowCLPicker(false); }}
          onClose={() => setShowCLPicker(false)}
        />
      )}

      {syncPrompt && (
        <TemplateSyncModal
          resumeTemplateName={syncPrompt.resumeTemplateName}
          coverLetterTemplateName={syncPrompt.matchingCLTemplate.name}
          onSync={handleSyncCoverLetters}
          onKeep={handleDismissSync}
        />
      )}

      {tourFading && <div className="tour-fade-overlay" />}
      <FeedbackTab />
    </div>
  );
}
